import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChatComposeIcon } from '../../assets/icons/chatCompose.svg';
import { Palette } from '../../components/utils';
import {
  clearChatThread,
  getChatConversations,
  selectChatConversation
} from '../../redux/actions/chat';
import { setGlobalBuildingId, setGlobalSelectedBuilding } from '../../redux/actions/building';
import { setToast } from '../../redux/actions/window';
import { requestPending, requestSuccess, requestFail } from '../../utils/status';
import { GET_CHAT_CONVERSATIONS } from '../../redux/constants';
import { chatScopeParams, chatErrorMessage, isBuildingChatEnabled, resolveBuildingList } from '../../helper/chat';
import { CONVERSATION_TYPE } from './constants';
import { UserRole } from '../users/constant';
import LoadingAvatar from '../../components/LoadingAvatar';
import { LOADING_BLUE } from '../../utils/colorCode';
import ChatConversationListItem from './ChatConversationListItem';
import ChatThread from './ChatThread';
import ChatNewConversation from './ChatNewConversation';
import ChatPanelHeader from './ChatPanelHeader';
import ChatDrawer from './ChatDrawer';
import ChatBuildingSelect from './ChatBuildingSelect';
import ChatSearchField from './ChatSearchField';
import { ChatEmptyText, ChatOlderLoadingWrap } from './chatSharedStyles';
import useDebouncedSearch from './useDebouncedSearch';
import { preloadChatAvatars } from './chatAvatarUtils';
import { setChatInboxSearchQ } from './chatInboxScope';

const LOAD_MORE_BOTTOM_PX = 120;

const ChatPanel = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const listBodyRef = useRef(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useDebouncedSearch(search);
  const [showNewChat, setShowNewChat] = useState(false);
  const [avatarsReady, setAvatarsReady] = useState(false);
  const { globalBuildingId, globalBuildingName, buildings, allBuildings } = useSelector(
    (state) => state.building
  );
  const globalOrgId = useSelector((state) => state.Organization?.globalOrgId);
  const roleId = Number(useSelector((state) => state.auth?.roleId)) || 0;
  const {
    conversations,
    listStatus,
    listError,
    listHasMore,
    listLoadingMore,
    listScope,
    page,
    selectedConversation
  } = useSelector((state) => state.chat);

  const buildingId = Number(globalBuildingId) || 0;
  const orgId = Number(globalOrgId) > 0 ? Number(globalOrgId) : 0;
  const needsOrg = roleId === UserRole.SUPER_ADMIN && orgId <= 0;
  const inThread = Boolean(selectedConversation?.id);

  const buildingOptions = useMemo(() => {
    const source = resolveBuildingList(allBuildings, buildings);
    const chatBuildings = source.filter(isBuildingChatEnabled);
    const options = [
      { id: 0, name: t('All Buildings') },
      ...chatBuildings.map((b) => ({ id: b.id, name: b.name }))
    ];
    const selected = source.find((b) => Number(b.id) === buildingId);
    if (
      buildingId > 0 &&
      globalBuildingName &&
      isBuildingChatEnabled(selected) &&
      !options.some((b) => Number(b.id) === buildingId)
    ) {
      options.push({ id: buildingId, name: globalBuildingName });
    }
    return options;
  }, [allBuildings, buildings, buildingId, globalBuildingName, t]);

  const selectedBuildingLabel =
    buildingId > 0
      ? globalBuildingName ||
        buildingOptions.find((b) => Number(b.id) === buildingId)?.name ||
        t('Building')
      : t('All Buildings');

  const directConversations = useMemo(() => {
    const directs = (conversations || []).filter(
      (c) => Number(c.conversation_type) === CONVERSATION_TYPE.DIRECT
    );
    // Drop rows from a stale wrong-building response if scope refetch raced.
    if (buildingId <= 0) return directs;
    return directs.filter((c) => Number(c.building_id) === buildingId);
  }, [conversations, buildingId]);


  const isLoading =
    listStatus === requestPending(GET_CHAT_CONVERSATIONS) && !listLoadingMore;
  const scopeMatches =
    Number(listScope?.building) === buildingId &&
    Number(listScope?.org) === orgId &&
    String(listScope?.q || '') === String(debouncedSearch || '');
  const isFailed =
    listStatus === requestFail(GET_CHAT_CONVERSATIONS) &&
    scopeMatches &&
    directConversations.length === 0;
  const isLoaded = listStatus === requestSuccess(GET_CHAT_CONVERSATIONS);
  // Loaded rows belong to another building/search: keep loader until matching fetch lands.
  const scopeStale = isLoaded && !scopeMatches;

  const avatarPreloadKey = useMemo(
    () => directConversations.map((c) => `${c.id}:${c.resident_avatar || ''}`).join('|'),
    [directConversations]
  );

  // Hide list until avatars are ready only on open / building / org / search changes.
  useEffect(() => {
    setAvatarsReady(false);
  }, [open, buildingId, orgId, debouncedSearch]);

  useEffect(() => {
    if (!open || !isLoaded) return undefined;
    if (directConversations.length === 0) {
      setAvatarsReady(true);
      return undefined;
    }

    let cancelled = false;
    preloadChatAvatars(directConversations.map((c) => c.resident_avatar)).then(() => {
      if (!cancelled) setAvatarsReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [open, isLoaded, avatarPreloadKey, directConversations]);

  useEffect(() => {
    if (!open) return;
    if (needsOrg) return;

    setChatInboxSearchQ(debouncedSearch);
    dispatch(
      getChatConversations({
        page: 1,
        replace: true,
        ...chatScopeParams({ building: buildingId, org: orgId, q: debouncedSearch })
      })
    );
  }, [open, buildingId, orgId, needsOrg, debouncedSearch, dispatch]);

  // Header org/building switch (and chat building filter) must leave thread / New Chat,
  // same as an explicit building change inside the panel.
  const prevScopeRef = useRef({ buildingId, orgId, ready: false });
  useEffect(() => {
    if (!open) {
      prevScopeRef.current = { buildingId, orgId, ready: false };
      return;
    }
    const prev = prevScopeRef.current;
    if (!prev.ready) {
      prevScopeRef.current = { buildingId, orgId, ready: true };
      return;
    }
    const changed = prev.buildingId !== buildingId || prev.orgId !== orgId;
    prevScopeRef.current = { buildingId, orgId, ready: true };
    if (!changed) return;
    setShowNewChat(false);
    dispatch(clearChatThread());
  }, [open, buildingId, orgId, dispatch]);

  // If a superseded realtime/panel race applied the wrong scope, refetch the UI scope.
  useEffect(() => {
    if (!open || needsOrg || !scopeStale) return;
    if (listStatus === requestPending(GET_CHAT_CONVERSATIONS)) return;
    setChatInboxSearchQ(debouncedSearch);
    dispatch(
      getChatConversations({
        page: 1,
        replace: true,
        ...chatScopeParams({ building: buildingId, org: orgId, q: debouncedSearch })
      })
    );
  }, [
    open,
    needsOrg,
    scopeStale,
    listStatus,
    buildingId,
    orgId,
    debouncedSearch,
    dispatch
  ]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebouncedSearch('');
      setShowNewChat(false);
      setAvatarsReady(false);
      setChatInboxSearchQ('');
      dispatch(clearChatThread());
    }
  }, [open, dispatch, setDebouncedSearch]);

  const searching = Boolean(search.trim());
  const showListLoading =
    isLoading ||
    scopeStale ||
    (isLoaded && scopeMatches && directConversations.length > 0 && !avatarsReady);
  const showEmptyCenter =
    showListLoading ||
    isFailed ||
    (isLoaded && scopeMatches && directConversations.length === 0);

  const loadMoreConversations = useCallback(() => {
    if (!open) return;
    if (needsOrg) return;
    if (!listHasMore || listLoadingMore || isLoading) return;
    if (listStatus === requestPending(GET_CHAT_CONVERSATIONS)) return;

    dispatch(
      getChatConversations({
        page: (Number(page) || 1) + 1,
        replace: false,
        ...chatScopeParams({ building: buildingId, org: orgId, q: debouncedSearch }),
        fail: (err) => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: chatErrorMessage(err, t('Failed to load more conversations')),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  }, [
    open,
    needsOrg,
    listHasMore,
    listLoadingMore,
    isLoading,
    listStatus,
    page,
    buildingId,
    orgId,
    debouncedSearch,
    dispatch,
    t
  ]);

  const handleListScroll = () => {
    const el = listBodyRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining > LOAD_MORE_BOTTOM_PX) return;
    loadMoreConversations();
  };

  // Fill the viewport with more pages only after the real list is painted.
  // Measuring during the avatar loader is wrong: Body is already "full height", so we
  // skip load-more and never retry when only a few directs remain after filtering.
  useEffect(() => {
    if (!open || inThread || showNewChat) return undefined;
    if (!isLoaded || scopeStale || showListLoading || !listHasMore || listLoadingMore) {
      return undefined;
    }
    const el = listBodyRef.current;
    if (!el) return undefined;

    let cancelled = false;
    const tryLoadMore = () => {
      if (cancelled) return;
      if (el.scrollHeight > el.clientHeight + 1) return;
      loadMoreConversations();
    };

    // After paint, and once more on the next frame in case row layout shifts.
    tryLoadMore();
    const raf = requestAnimationFrame(tryLoadMore);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [
    open,
    inThread,
    showNewChat,
    isLoaded,
    scopeStale,
    showListLoading,
    listHasMore,
    listLoadingMore,
    directConversations.length,
    loadMoreConversations
  ]);

  const handleBuildingChange = (e) => {
    const nextId = Number(e.target.value) || 0;
    const option = buildingOptions.find((b) => Number(b.id) === nextId);
    const label = option?.name || (nextId === 0 ? t('All Buildings') : '');
    setShowNewChat(false);
    dispatch(clearChatThread());
    dispatch(
      setGlobalBuildingId({
        globalBuildingId: nextId,
        globalBuildingName: nextId === 0 ? '' : label
      })
    );
    dispatch(
      setGlobalSelectedBuilding(
        nextId === 0
          ? { value: 0, label: t('All Buildings') }
          : { value: nextId, label, id: nextId, name: label }
      )
    );
  };

  const handleClose = () => {
    setShowNewChat(false);
    dispatch(clearChatThread());
    onClose();
  };

  const openConversation = (conversation) => {
    setShowNewChat(false);
    dispatch(selectChatConversation(conversation));
  };

  const newChatBuildings = useMemo(
    () => buildingOptions.filter((b) => Number(b.id) > 0),
    [buildingOptions]
  );

  const openNewChat = () => {
    setShowNewChat(true);
  };

  if (!open) return null;

  if (inThread) {
    return (
      <ChatDrawer ariaLabel={t('Messages')} onClose={handleClose}>
        <ChatThread
          conversation={selectedConversation}
          onBack={() => dispatch(clearChatThread())}
          onClose={handleClose}
        />
      </ChatDrawer>
    );
  }

  if (showNewChat) {
    return (
      <ChatDrawer ariaLabel={t('New Chat')} onClose={handleClose}>
        <ChatNewConversation
          buildings={newChatBuildings}
          initialBuildingId={buildingId}
          onBack={() => setShowNewChat(false)}
          onClose={handleClose}
          onStarted={() => setShowNewChat(false)}
        />
      </ChatDrawer>
    );
  }

  let body;
  if (needsOrg) {
    body = <ChatEmptyText>{t('Select an organization to view messages')}</ChatEmptyText>;
  } else if (showListLoading) {
    body = <LoadingAvatar color={LOADING_BLUE} height="20vh" />;
  } else if (isFailed) {
    body = <ChatEmptyText>{listError || t('Failed to load conversations')}</ChatEmptyText>;
  } else if (isLoaded && scopeMatches && directConversations.length === 0) {
    body = (
      <ChatEmptyText>
        {searching ? t('No matching conversations') : t('No conversations yet')}
      </ChatEmptyText>
    );
  } else {
    body = (
      <List>
        {directConversations.map((conversation) => (
          <ChatConversationListItem
            key={conversation.id}
            conversation={conversation}
            onClick={openConversation}
          />
        ))}
        {listLoadingMore && (
          <ChatOlderLoadingWrap>
            <LoadingAvatar color={LOADING_BLUE} height="48px" />
          </ChatOlderLoadingWrap>
        )}
      </List>
    );
  }

  return (
    <ChatDrawer ariaLabel={t('Messages')} onClose={handleClose}>
      <ChatPanelHeader title={t('Messages')} onClose={handleClose} />

      <Filters>
        <ChatBuildingSelect
          value={buildingId}
          onChange={handleBuildingChange}
          options={buildingOptions}
          ariaLabel={selectedBuildingLabel}
          variant="filled"
        />
        <ChatSearchField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('Search messages')}
          ariaLabel={t('Search messages')}
          onClear={() => {
            setSearch('');
            setDebouncedSearch('');
          }}
        />
      </Filters>

      <Body ref={listBodyRef} centered={showEmptyCenter} onScroll={handleListScroll}>
        {body}
      </Body>

      <ComposeButton
        type="button"
        aria-label={t('New chat')}
        title={t('New chat')}
        onClick={openNewChat}
      >
        <ChatComposeIcon width={30} height={30} aria-hidden="true" />
      </ComposeButton>
    </ChatDrawer>
  );
};

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  gap: 16px;
  padding: 20px;
  background: ${Palette.WHITE};
  flex-shrink: 0;
  position: relative;
  z-index: 5;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  position: relative;
  ${({ centered }) =>
    centered
      ? `
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
  `
      : ''}
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const ComposeButton = styled.button`
  position: absolute;
  right: 1.25rem;
  bottom: 1.5rem;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${Palette.HAZY_BLUE};
  color: ${Palette.WHITE};
  line-height: 0;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(19, 49, 85, 0.28);
  z-index: 1;

  & > svg {
    display: block;
    flex-shrink: 0;
  }

  &:hover {
    background: ${Palette.OCEANIC_NAV_HOVER};
  }

  &:focus {
    outline: none;
  }
`;

export default ChatPanel;
